/**
 * Channels: the social accounts a workspace posts to, and the posts waiting to go
 * ([25](../../../docs/25-channels-and-publishing.md),
 * [ADR 0038](../../../docs/adr/0038-a-channel-is-a-connection-a-post-is-a-row-and-the-worker-is-the-only-thing-that-publishes.md)).
 *
 * Six platforms, one shape. Connecting is the same three steps everywhere — a consent URL, a callback that claims a
 * state exactly once, a sealed token — and publishing is one function that takes an account and a post and gives
 * back an id. What differs between Instagram and LinkedIn is inside the adapter; nothing above it branches on a
 * platform name except to draw a logo and to say what a platform will and will not accept.
 *
 * Nothing here carries a token. The tokens are the one secret a customer hands us, sealed at rest and opened only in
 * the worker on the line before the provider, which is why `ChannelView` has no field one could go in — the same
 * rule the mailbox follows ([22](../../../docs/22-the-mailbox-connection.md)).
 */

/* ── the platforms ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The six the old Joogoos published to, carried over whole.
 *
 * [08](../../../docs/08-prior-art.md) said "six publishing platforms at once" was left behind and v1 would have one.
 * That was a judgement about *effort*, and the effort turns out to be already spent: the old repo's six token
 * providers and six post providers are working code with the platform quirks discovered the hard way in them. The
 * owner asked for all six on 2026-09-20 and they come across together (ADR 0038 §why six).
 */
export const CHANNEL_PLATFORMS = ["instagram", "facebook", "tiktok", "youtube", "linkedin", "x"] as const;
export type ChannelPlatform = (typeof CHANNEL_PLATFORMS)[number];

/**
 * The networks whose numbers Joogoos reads back each night, with the permissions they already hold (25). TikTok needs
 * `video.list` and `user.info.stats`, LinkedIn a product of its own, and a read on X is billed, so those three say
 * they are not read rather than showing a zero. YouTube also waits for `METRICS_READ_YOUTUBE` on the box.
 */
export const READ_PLATFORMS = ["youtube", "instagram", "facebook"] as const;
export type ReadPlatform = (typeof READ_PLATFORMS)[number];
export const isReadPlatform = (p: string): p is ReadPlatform => (READ_PLATFORMS as readonly string[]).includes(p);

/**
 * `connected` · `disconnected` (the platform refused the token and only reconnecting fixes it) · `paused` (the
 * customer's own switch, which keeps the token). A channel that is not `connected` publishes nothing.
 */
export const CHANNEL_STATUSES = ["connected", "disconnected", "paused"] as const;
export type ChannelStatus = (typeof CHANNEL_STATUSES)[number];

/** What a platform will accept in a post. The Studio reads it before it offers a channel for a piece of content. */
export interface PlatformTraits {
  /** What the page calls it. */
  label: string;
  /** A post with no picture and no video: only two of the six take one. */
  textAlone: boolean;
  /** Nothing but video, whatever else is attached. */
  videoOnly: boolean;
  /** The longest caption the platform keeps. Longer is refused before the post is ever scheduled, not after. */
  captionMax: number;
  /**
   * The video it will take through its API, which is sometimes less than its own app takes (2026-09-26, the owner:
   * *"we need the limit that the social impose"*). A file and a length outside these fail at the platform, after the
   * post was scheduled, so they are refused here instead, like a caption that is too long.
   *
   * Null where Joogoos's connector posts no video at all, whatever the platform itself takes: LinkedIn, whose video
   * upload is not built (32). Every video for it is refused when it is scheduled, not when it fails to publish.
   */
  video: { maxBytes: number; minMs: number; maxMs: number } | null;
  /**
   * The pictures it will take through its API, where it limits them (32): Instagram an 8 MB picture between 4:5 and
   * 1.91:1, X a 5 MB one. `ratio` is width over height, both ends included.
   */
  image?: { maxBytes?: number; ratio?: readonly [number, number] };
  /** Pages the customer is asked to agree to when connecting, the platform's rule (YouTube's API Services terms). */
  terms?: ReadonlyArray<{ label: string; url: string }>;
  /**
   * What the customer has to have before the button will work, said in the words the page shows. Every one of these
   * was a support question in the old Joogoos.
   */
  needs: string;
  /**
   * What the platform's own screen is about to ask, and what to check on it before agreeing — said under *Connect*
   * so nobody authorises the wrong account. X offers whichever account the browser is signed in to; Facebook asks
   * which Page; LinkedIn connects the person, never a company page. Found on dev on 2026-09-26, connecting X.
   */
  before: string;
}

const MB = 1024 * 1024, S = 1000, MIN = 60 * S;

/**
 * The six, and what each one is fussy about.
 *
 * The numbers are the platforms' own published limits as of September 2026, and they are here rather than in the
 * adapters because the *page* has to know them: a caption refused after a customer has scheduled a week of posts is
 * a week of posts that quietly did not go.
 *
 * The video limits are the ones for publishing through each API, checked on 2026-09-26. Where a platform raises them
 * for some accounts — TikTok and X for paying or chosen creators, YouTube once a channel is verified — this is the
 * limit every account has. Facebook's is a Reel's, because a video goes to a Page as a Reel (`social/facebook.ts`),
 * and Meta names no largest file for one, so 1 GB is the figure the tools that post them work to.
 */
export const PLATFORMS: Record<ChannelPlatform, PlatformTraits> = {
  instagram: {
    label: "Instagram", textAlone: false, videoOnly: false, captionMax: 2200,
    image: { maxBytes: 8 * MB, ratio: [4 / 5, 1.91] },
    video: { maxBytes: 300 * MB, minMs: 3 * S, maxMs: 15 * MIN },
    needs: "a Business or Creator account — Instagram refuses to publish from a personal one",
    before: "You sign in on Instagram itself, as the business's account, and allow Joogoos to publish",
  },
  facebook: {
    label: "Facebook", textAlone: true, videoOnly: false, captionMax: 63_206,
    video: { maxBytes: 1024 * MB, minMs: 3 * S, maxMs: 90 * S },
    needs: "a Facebook Page you administer; posts go to the Page, never to your own timeline",
    before: "You sign in on Facebook and tick the Page this business posts as",
  },
  tiktok: {
    label: "TikTok", textAlone: false, videoOnly: true, captionMax: 2200,
    // 64 MB, not TikTok's 4 GB: the connector sends a video in one piece, and TikTok takes one piece only up to
    // 64 MB (its media transfer guide). Larger would need chunking, which no video this product makes needs yet.
    video: { maxBytes: 64 * MB, minMs: 3 * S, maxMs: 10 * MIN },
    needs: "video only, and every post asks you for its privacy setting — TikTok requires that to be your choice",
    before: "You sign in on TikTok as the business's account and allow Joogoos to post videos",
  },
  youtube: {
    label: "YouTube", textAlone: false, videoOnly: true, captionMax: 5000,
    // YouTube's API Services policies require both, linked where YouTube is connected (32).
    terms: [
      { label: "YouTube Terms of Service", url: "https://www.youtube.com/t/terms" },
      { label: "Google Privacy Policy", url: "https://policies.google.com/privacy" },
    ],
    video: { maxBytes: 256 * 1024 * MB, minMs: 1 * S, maxMs: 15 * MIN },
    needs: "a channel on the Google account you connect; a vertical or square video up to three minutes is published as a Short",
    before: "You sign in with Google and choose the account that owns the business's YouTube channel",
  },
  linkedin: {
    label: "LinkedIn", textAlone: true, videoOnly: false, captionMax: 3000,
    // LinkedIn takes video up to 500 MB and 30 min, but through a multi-part upload the connector does not do yet,
    // and it refused every video at publish. Until it does, a video is refused here, before it is on a calendar.
    video: null,
    needs: "nothing but the sign-in; posts go out as you, on your own profile, not on a company page",
    before: "You sign in on LinkedIn as yourself: the posts carry your name",
  },
  x: {
    label: "X", textAlone: true, videoOnly: false, captionMax: 280,
    image: { maxBytes: 5 * MB },
    video: { maxBytes: 512 * MB, minMs: 500, maxMs: 140 * S },
    needs: "X charges for every post made through its API, and more for one with a link in it",
    before: "X connects the account shown at the top of its screen — switch to the business's account there before you authorise",
  },
};

/**
 * How long a caption is, the way its platform counts it.
 *
 * Characters everywhere but X, which counts its own way ([31](../../../docs/31-posts-joogoos-drafts.md)): a link is
 * 23 whatever its length, most characters outside the Latin scripts count two, and so does every emoji, however
 * many code points it is made of. A count that disagreed with X's would pass a post here that X then refuses — or,
 * on a post made by hand, that X cuts off in the owner's compose box.
 */
export function captionLength(platform: ChannelPlatform, caption: string): number {
  return platform === "x" ? xLength(caption) : [...caption].length;
}

/**
 * Whether a caption carries a link, the way X counts one — which X prices differently: a post with a link costs
 * $0.20 through its API, one without $0.015 (docs.x.com pricing, read 2026-09-26; 31, 32).
 */
export function hasLink(caption: string): boolean {
  LINK.lastIndex = 0;
  return LINK.test(caption.normalize("NFC"));
}
export const X_LINK_NOTE = "This post has a link in it, and X charges $0.20 for posting one through its API instead of $0.015.";

/** Whether a caption fits, which the page checks while it is being typed and the API checks again. */
export function captionFits(platform: ChannelPlatform, caption: string): boolean {
  return captionLength(platform, caption) <= PLATFORMS[platform].captionMax;
}

/** What X counts a link as, however long it is. */
const X_LINK = 23;

/**
 * A link, as X finds one: with a scheme, starting `www.`, or a bare name ending in a top-level domain people use.
 * Generous on purpose — counting something as a link that X does not costs a few characters of room, and missing
 * one X does find lets a post through that X refuses.
 */
const LINK = /\b(?:https?:\/\/\S+|www\.\S+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|co|uk|app|dev|ai|eu|fr|de|es|it|nl|be|ie|us|ca|au|info|biz|me|tv|ly|so|gg|xyz|shop|store|online|site|tech)\b(?:\/\S*)?)/giu;

/** The code points X counts as one; everything else is two. Its own table (twitter-text, config v3). */
const X_LIGHT: ReadonlyArray<readonly [number, number]> = [[0, 4351], [8192, 8205], [8208, 8223], [8242, 8247]];

function xLength(caption: string): number {
  const text = caption.normalize("NFC");
  let links = 0;
  const rest = text.replace(LINK, () => { links += 1; return ""; });
  let n = links * X_LINK;
  for (const { segment } of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(rest)) {
    if (/\p{Extended_Pictographic}/u.test(segment)) { n += 2; continue; }
    for (const ch of segment) {
      const cp = ch.codePointAt(0)!;
      n += X_LIGHT.some(([a, b]) => cp >= a && cp <= b) ? 1 : 2;
    }
  }
  return n;
}

/**
 * Whether a platform would take this post at all, in the words the page shows, or null when it would.
 *
 * One function so the Studio, the calendar and the API give the same answer in the same words. It is about the
 * *shape* of the post and never about the account: a disconnected channel is a different refusal, said elsewhere.
 */
export function refusedBecause(platform: ChannelPlatform, post: {
  hasMedia: boolean; isVideo: boolean; caption: string;
  /** A video's length and size, where they are known. Unknown is not refused: the platform has the last word. */
  durationMs?: number | null | undefined; bytes?: number | null | undefined;
  /** A picture's shape, where it is known, for the platforms that limit it. */
  width?: number | null | undefined; height?: number | null | undefined;
}): string | null {
  const t = PLATFORMS[platform];
  // The video-only platforms are asked first, so a post with nothing attached is told what TikTok actually wants —
  // "needs a video" — rather than the more general "a picture or a video", which would send somebody off to find a
  // photograph TikTok is never going to take.
  if (t.videoOnly && !post.isVideo) return post.hasMedia ? `${t.label} takes video only.` : `${t.label} needs a video.`;
  if (!post.hasMedia && !t.textAlone) return `${t.label} needs a picture or a video.`;
  if (!captionFits(platform, post.caption)) return `${t.label} keeps ${t.captionMax.toLocaleString()} characters; this is ${captionLength(platform, post.caption).toLocaleString()}.`;
  if (post.isVideo && !t.video) return `${t.label} takes a picture or words from Joogoos, not a video yet.`;
  if (post.isVideo && t.video) {
    const v = t.video, ms = post.durationMs ?? null, bytes = post.bytes ?? null;
    if (ms !== null && ms > v.maxMs) return `${t.label} takes videos up to ${lengthOf(v.maxMs)}; this one is ${lengthOf(ms)}.`;
    if (ms !== null && ms < v.minMs) return `${t.label} takes videos of at least ${lengthOf(v.minMs)}; this one is ${lengthOf(ms)}.`;
    if (bytes !== null && bytes > v.maxBytes) return `${t.label} takes videos up to ${sizeOf(v.maxBytes)}; this one is ${sizeOf(bytes)}.`;
  }
  if (post.hasMedia && !post.isVideo && t.image) {
    const bytes = post.bytes ?? null, w = post.width ?? null, h = post.height ?? null;
    if (t.image.maxBytes && bytes !== null && bytes > t.image.maxBytes) {
      return `${t.label} takes pictures up to ${sizeOf(t.image.maxBytes)}; this one is ${sizeOf(bytes)}.`;
    }
    const ratio = t.image.ratio;
    if (ratio && w && h && (w / h < ratio[0] - 0.005 || w / h > ratio[1] + 0.005)) {
      return `${t.label} takes pictures from 4:5 (tall) to 1.91:1 (wide); this one is ${w}×${h}.`;
    }
  }
  return null;
}

/** A length as a person says it: "2 min 20 s", "90 s", "15 min". Half a second is said as it is, not as nothing. */
function lengthOf(ms: number): string {
  if (ms < 1000) return `${Math.round(ms / 100) / 10} s`;
  const all = Math.round(ms / 1000), m = Math.floor(all / 60), s = all % 60;
  return all > 90 ? `${m} min${s ? ` ${s} s` : ""}` : `${all} s`;
}

/** A size in MB, or GB from a thousand MB up. */
function sizeOf(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1000 ? `${Math.round(mb / 102.4) / 10} GB` : `${Math.round(mb)} MB`;
}

/* ── a channel ───────────────────────────────────────────────────────────────────────────────────────────────── */

/** A connected account as the page draws it. Everything the browser may know, and nothing else. */
export interface ChannelView {
  id: string;
  platform: ChannelPlatform;
  /** What the platform calls this account — a Page's name, a handle, a channel title. */
  accountName: string;
  /** The `@handle` where there is one, without the at sign, or null. */
  handle: string | null;
  avatarUrl: string | null;
  status: ChannelStatus;
  connectedAt: string;
  lastPublishedAt: string | null;
  /**
   * Whatever the platform last refused with, in words, or null. Never a token: the adapters are given the
   * platform's message and take the token out of it before it is ever stored.
   */
  lastError: string | null;
  /**
   * True when the refresh has failed in a way retrying cannot fix — a revoked grant, an expired refresh token.
   * The page says *reconnect* and every scheduled post on this channel stops rather than failing one by one.
   */
  needsReauth: boolean;
  /** How many posts are waiting to go out on this channel. */
  waiting: number;
  /** The accounts this connection can post as, when there is a choice — a Facebook account's Pages (32). */
  choices: Array<{ id: string; name: string }>;
  /** Which of them it posts as now, or null where there is no choice. */
  choiceId: string | null;
  /** When the permission runs out, or null when the platform did not say. */
  expiresAt: string | null;
  /**
   * Whether Joogoos can renew it before then. LinkedIn issues no renewal to an app like this one, so its connection
   * simply ends after sixty days and the page says by when to connect again (32).
   */
  renewable: boolean;
  /** YouTube only: the channel's usual answer to "made for kids?", or null to be asked on every video (32). */
  madeForKids: boolean | null;
  /** The account's own public page, which its name on the row opens (`accountUrl`), or null where none is known. */
  url: string | null;
}

/**
 * A connected account's own public page, from what the connection keeps, or null where it keeps nothing that names
 * one. Built on the API, where the ids are, so the browser is handed an address and never an id it has no use for.
 *
 * - **Facebook** is the Page the row posts to — the one picked, not the first the sign-in listed — never the person
 *   who connected it.
 * - **YouTube** is the handle, and the channel id when there is none; every channel has an id.
 * - **LinkedIn** is nothing: its sign-in gives the person's id, and LinkedIn has no address that takes one.
 * - **TikTok** needs the username, which its connection learns from `creator_info`; its open id names nobody.
 */
export function accountUrl(platform: ChannelPlatform, account: { handle: string | null; accountId: string; pageId: string | null }): string | null {
  const handle = account.handle ? encodeURIComponent(account.handle) : null;
  switch (platform) {
    case "instagram": return handle ? `https://www.instagram.com/${handle}` : null;
    case "facebook": return account.pageId ? `https://www.facebook.com/${encodeURIComponent(account.pageId)}` : null;
    case "tiktok": return handle ? `https://www.tiktok.com/@${handle}` : null;
    case "youtube":
      return handle ? `https://www.youtube.com/@${handle}`
        : account.accountId ? `https://www.youtube.com/channel/${encodeURIComponent(account.accountId)}` : null;
    case "linkedin": return null;
    case "x": return handle ? `https://x.com/${handle}` : null;
  }
}

/** `GET /v1/channels`. */
export interface ChannelsResponse {
  /** False when this box has no key for any platform, the way every other unbuilt screen answers. */
  available: boolean;
  channels: ChannelView[];
  /** The platforms this box actually holds credentials for; the rest are drawn greyed with a word about why. */
  connectable: ChannelPlatform[];
  /** Only an owner may connect or disconnect one — a channel is the business's public voice (25). */
  mayConnect: boolean;
  note?: string;
}

/** `POST /v1/channels/:platform/connect`: where to send the customer for the platform's own sign-in. */
export interface ConnectChannelResponse {
  url: string;
}

/** `PATCH /v1/channels/:id`: the one thing a customer may change about a connection that is not connecting it again. */
export interface ChannelSettings {
  /** The workspace's own pause, which is not a disconnection: the token stays and nothing goes out. */
  paused?: boolean | undefined;
  /** Post as another of the connection's `choices` — the Facebook Page this business posts as (32). */
  choiceId?: string | undefined;
  /** YouTube only: the channel's usual answer to "made for kids?", null to ask every time (32). */
  madeForKids?: boolean | null | undefined;
}

/* ── a scheduled post ────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * `draft` · `scheduled` · `publishing` (a worker has claimed it) · `published` · `failed` · `cancelled` · `rejected`.
 *
 * `publishing` is the claim, and it is what stops two workers publishing the same post twice. The old Joogoos
 * learned this the hard way and its answer — an atomic status change, plus a watchdog for anything left claimed —
 * comes across unchanged (08, ADR 0038).
 *
 * `draft` is a post nobody has agreed to yet, and `rejected` one an owner said no to
 * ([31](../../../docs/31-posts-joogoos-drafts.md), ADR 0056). Only an owner's approval moves a draft on, and it is
 * what gives it its time.
 */
export const POST_STATUSES = ["draft", "scheduled", "publishing", "published", "failed", "cancelled", "rejected"] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/** One post, on one channel, at one time. A post to three channels is three of these. */
export interface ScheduledPostView {
  id: string;
  /** Null once the account this went to has been disconnected; `platform` still says where it went. */
  channelId: string | null;
  platform: ChannelPlatform;
  /**
   * The account's name at the moment the page was drawn, so a list reads without a second lookup — or the
   * platform's own name once the account has been disconnected, which is the honest answer: the post went to
   * Instagram, and which Instagram account is no longer ours to say.
   */
  accountName: string;
  /** The project this came out of, or null for something written straight into the calendar. */
  projectId: string | null;
  caption: string;
  /** What is being posted, as the browser can show it. Null for a text-only post. */
  thumbUrl: string | null;
  isVideo: boolean;
  scheduledAt: string;
  status: PostStatus;
  /** Where it landed, once it has. */
  externalUrl: string | null;
  publishedAt: string | null;
  /** Why it did not go, in the platform's words, or null. */
  error: string | null;
  /** How many times publishing has been tried. At `PUBLISH_ATTEMPTS` it fails for good. */
  attempts: number;
  /**
   * What Joogoos drew a draft it wrote from — a section of the business page or a source, by its title — shown beside
   * the draft so approving is reading the two side by side (31 §the drafter). Absent or null for a person's post.
   */
  source?: string | null;
}

/** `GET /v1/posts?from=&to=`. */
export interface ScheduledPostsResponse {
  available: boolean;
  posts: ScheduledPostView[];
  note?: string;
}

/** `POST /v1/posts`: one call schedules the same content to several channels, which is how people think about it. */
export interface SchedulePostRequest {
  /** One row is created per channel. Every one of them must be connected and in this workspace. */
  channelIds: string[];
  /** The project whose rendered result is being posted, when there is one. */
  projectId?: string | undefined;
  /** The picture or video, by id. Absent is a text-only post, which only Facebook, LinkedIn and X will take. */
  mediaId?: string | undefined;
  caption: string;
  /** ISO, and in the future. A time already past is refused rather than published at once. */
  scheduledAt: string;
  /**
   * What each network asks per post, by platform: `{ tiktok: TikTokChoices }`. Each row keeps only its own
   * network's, so TikTok's privacy choice never travels onto the Instagram row (32).
   */
  options?: Partial<Record<ChannelPlatform, unknown>> | undefined;
}

/** `PATCH /v1/posts/:id`: move it, or change what it says, while it is still a draft or still waiting. */
export interface UpdateScheduledPost {
  caption?: string | undefined;
  scheduledAt?: string | undefined;
}

/* ── the queue: drafts, and what an owner posts by hand ──────────────────────────────────────────────────────── */

/**
 * Where an owner goes to post something by hand, when no connector will ([31](../../../docs/31-posts-joogoos-drafts.md)).
 *
 * `prefilled` says whether the page opens with the text already in it. X's does; a LinkedIn company page's cannot, so
 * the text goes on the clipboard and the page says *paste*.
 */
export interface HandOff {
  url: string;
  /** Which account to be signed in as, when the business page says — `@handle`, or a company page's name. */
  as: string | null;
  prefilled: boolean;
}

/** A post in the queue, with how it will go out: `handOff` is null when a connected channel will publish it. */
export interface QueuedPost extends ScheduledPostView {
  handOff: HandOff | null;
}

/** `GET /v1/posts/queue`. */
export interface PostQueueResponse {
  /** Waiting for an owner, oldest first. */
  drafts: QueuedPost[];
  /** Approved, going out by hand and not yet posted, soonest first. Due ones are the top of the page. */
  byHand: QueuedPost[];
  /** Only an owner approves, and only an owner says a by-hand post is up (31). */
  mayApprove: boolean;
}

/** `POST /v1/posts/drafts`: a post written by a person, waiting for an owner like one Joogoos wrote. */
export interface DraftPostRequest {
  platform: ChannelPlatform;
  caption: string;
  /** When it is proposed to go out. Absent is this time tomorrow; the owner sets the real time on approval. */
  scheduledAt?: string | undefined;
  mediaId?: string | undefined;
}

/** `POST /v1/posts/:id/approve`. The time is the owner's, and at least `POST_MIN_LEAD_MS` ahead. */
export interface ApprovePostRequest {
  scheduledAt: string;
}

/** `POST /v1/posts/:id/posted`: the owner put it up by hand. The address, when they have it, is kept. */
export interface PostedByHandRequest {
  url?: string | undefined;
}

/** The first line of the business page's links that matches, or null. */
function linkTo(links: string, pattern: RegExp): RegExpMatchArray | null {
  for (const line of links.split("\n")) {
    const m = line.match(pattern);
    if (m) return m;
  }
  return null;
}

/** Addresses on x.com that are pages of X's, not accounts. */
const X_NOT_ACCOUNTS = new Set(["intent", "home", "i", "share", "search", "explore", "settings", "compose", "hashtag"]);

/**
 * Where to post this by hand, read from the business page's *Where we are online* section.
 *
 * X's compose page takes the text. A LinkedIn company page has no address that opens its share box with text in it,
 * so it opens the page's admin view, where the owner presses Create and pastes; without a company page, LinkedIn's
 * own share box takes the text on a computer. The rest open where their own posting starts, text on the clipboard.
 */
export function handOffFor(platform: ChannelPlatform, caption: string, links: string): HandOff {
  const text = encodeURIComponent(caption);
  switch (platform) {
    case "x": {
      const m = linkTo(links, /(?:^|[/\s.])(?:x|twitter)\.com\/@?([A-Za-z0-9_]{1,15})\b/i);
      const handle = m && !X_NOT_ACCOUNTS.has(m[1]!.toLowerCase()) ? `@${m[1]}` : null;
      return { url: `https://x.com/intent/tweet?text=${text}`, as: handle, prefilled: true };
    }
    case "linkedin": {
      const m = linkTo(links, /linkedin\.com\/company\/([^/\s?#]+)/i);
      return m
        ? { url: `https://www.linkedin.com/company/${m[1]}/admin/`, as: m[1]!, prefilled: false }
        : { url: `https://www.linkedin.com/feed/?shareActive=true&text=${text}`, as: null, prefilled: true };
    }
    case "facebook": {
      const m = linkTo(links, /(https?:\/\/(?:www\.)?facebook\.com\/[^\s?#]+)/i);
      return { url: m?.[1] ?? "https://www.facebook.com/", as: null, prefilled: false };
    }
    case "instagram": return { url: "https://www.instagram.com/", as: null, prefilled: false };
    case "tiktok": return { url: "https://www.tiktok.com/upload", as: null, prefilled: false };
    case "youtube": return { url: "https://studio.youtube.com/", as: null, prefilled: false };
  }
}

/* ── the numbers both sides use ──────────────────────────────────────────────────────────────────────────────── */

/**
 * How long a half-finished connection may sit before it is worthless. The same ten minutes the mailbox uses, for
 * the same reason: it is a person crossing to another site and back.
 */
export const CHANNEL_CONNECT_TTL_MS = 10 * 60 * 1000;

/**
 * How far past its time a post may still go out.
 *
 * Beyond this it is stale and fails instead. Marketing content is about a moment — a box that was down for two days
 * should not wake up and publish Tuesday's post on Thursday. Carried from the old `ScheduledPosts:StaleAfterHours`.
 */
export const POST_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * How long a post may sit claimed (`publishing`) before the watchdog takes it back.
 *
 * Longer than the slowest publish can be: one request may run ten minutes (`ask`) and a video upload makes several, so
 * ten minutes here could reclaim a post still going out and publish it twice (2026-10-31).
 */
export const POST_STUCK_AFTER_MS = 30 * 60 * 1000;

/** How many times a post is tried before it is failed for good and the customer is shown why. */
export const PUBLISH_ATTEMPTS = 3;

/** The soonest a post may be scheduled: far enough ahead that the sweeper will see it before it is due. */
export const POST_MIN_LEAD_MS = 60 * 1000;

/**
 * Refresh a token this long before it expires rather than at the moment it does.
 *
 * Five minutes, from the old repo, which found the alternative was a post failing because the token died between
 * the check and the call.
 */
export const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
